import React from 'react';
import { ActionButton, type ActionButtonData } from '../action-button/ActionButton';
import { t } from '../../services/locale';

export type ProfilePaymentMethodKind = 'direct_debit' | 'card';

export type ProfilePaymentMethodsData = {
  branchAcceptsDirectDebit?: boolean;
  branchAcceptsCard?: boolean;
  isChildAccount?: boolean;
  hasMandate?: boolean;
  hasCard?: boolean;
  /** Same flag as `ProfileController.hasSubscriptionMembership` (delete vs replace). */
  hasSubscriptionMembership?: boolean;
  mandateLabel?: string;
  cardLabel?: string;
  addMandateActionData?: ActionButtonData;
  deleteMandateActionData?: ActionButtonData;
  replaceMandateActionData?: ActionButtonData;
  addCardActionData?: ActionButtonData;
  deleteCardActionData?: ActionButtonData;
  replaceCardActionData?: ActionButtonData;
  onAddOrReplacePayment?: (method: ProfilePaymentMethodKind) => void;
  onDeletePayment?: (method: ProfilePaymentMethodKind) => void;
};

type Props = Readonly<{
  data?: ProfilePaymentMethodsData;
}>;

type PaymentRowConfig = {
  kind: ProfilePaymentMethodKind;
  accepted: boolean;
  saved: boolean;
  emptyLabel: string;
  savedLabel?: string;
  savedLabelId: string;
  showCardIcon?: boolean;
  addAction?: ActionButtonData;
  deleteAction?: ActionButtonData;
  replaceAction?: ActionButtonData;
};

type PaymentRowAction = {
  action?: ActionButtonData;
  onClick: () => void;
};

function PaymentMethodAction({
  action,
  onClick,
}: Readonly<{
  action?: ActionButtonData;
  onClick: () => void;
}>) {
  if (!action) {
    return null;
  }
  return (
    <span className="profile-action-button payment-method-action-button">
      <ActionButton data={{ ...action, onClick }} />
    </span>
  );
}

function paymentRowAction({
  row,
  isChildAccount,
  hasSubscriptionMembership,
  onAddOrReplacePayment,
  onDeletePayment,
}: Readonly<{
  row: PaymentRowConfig;
  isChildAccount?: boolean;
  hasSubscriptionMembership?: boolean;
  onAddOrReplacePayment?: (method: ProfilePaymentMethodKind) => void;
  onDeletePayment?: (method: ProfilePaymentMethodKind) => void;
}>): PaymentRowAction | null {
  if (isChildAccount) {
    return null;
  }
  if (!row.saved) {
    return {
      action: row.addAction,
      onClick: () => onAddOrReplacePayment?.(row.kind),
    };
  }
  if (hasSubscriptionMembership) {
    return {
      action: row.deleteAction,
      onClick: () => onDeletePayment?.(row.kind),
    };
  }
  return {
    action: row.replaceAction,
    onClick: () => onAddOrReplacePayment?.(row.kind),
  };
}

function PaymentMethodRowLabel({ row }: Readonly<{ row: PaymentRowConfig }>) {
  const label = row.saved ? (
    <p id={row.savedLabelId}>{row.savedLabel}</p>
  ) : (
    <p>{row.emptyLabel}</p>
  );

  if (row.showCardIcon) {
    return (
      <div>
        <p className="icon-card" />
        {label}
      </div>
    );
  }

  return <div className="list-text-left">{label}</div>;
}

function PaymentMethodRow({
  row,
  isChildAccount,
  hasSubscriptionMembership,
  onAddOrReplacePayment,
  onDeletePayment,
}: Readonly<{
  row: PaymentRowConfig;
  isChildAccount?: boolean;
  hasSubscriptionMembership?: boolean;
  onAddOrReplacePayment?: (method: ProfilePaymentMethodKind) => void;
  onDeletePayment?: (method: ProfilePaymentMethodKind) => void;
}>) {
  if (!row.accepted) {
    return null;
  }

  const rowAction = paymentRowAction({
    row,
    isChildAccount,
    hasSubscriptionMembership,
    onAddOrReplacePayment,
    onDeletePayment,
  });

  return (
    <div>
      <ul className="list">
        <li className="buttonRow">
          <PaymentMethodRowLabel row={row} />
          {rowAction ? (
            <div className="list-text-right">
              <PaymentMethodAction action={rowAction.action} onClick={rowAction.onClick} />
            </div>
          ) : null}
        </li>
      </ul>
    </div>
  );
}

export function ProfilePaymentMethods({ data }: Props) {
  if (!data) {
    return null;
  }

  const rows: PaymentRowConfig[] = [
    {
      kind: 'direct_debit',
      accepted: Boolean(data.branchAcceptsDirectDebit),
      saved: Boolean(data.hasMandate),
      emptyLabel: t('WEB_DIRECT_DEBIT'),
      savedLabel: data.mandateLabel,
      savedLabelId: 'saved-direct-debit-label',
      addAction: data.addMandateActionData,
      deleteAction: data.deleteMandateActionData,
      replaceAction: data.replaceMandateActionData,
    },
    {
      kind: 'card',
      accepted: Boolean(data.branchAcceptsCard),
      saved: Boolean(data.hasCard),
      emptyLabel: t('WEB_CARD'),
      savedLabel: data.cardLabel,
      savedLabelId: 'saved-card-label',
      showCardIcon: true,
      addAction: data.addCardActionData,
      deleteAction: data.deleteCardActionData,
      replaceAction: data.replaceCardActionData,
    },
  ];

  return (
    <>
      {rows.map((row) => (
        <PaymentMethodRow
          key={row.kind}
          row={row}
          isChildAccount={data.isChildAccount}
          hasSubscriptionMembership={data.hasSubscriptionMembership}
          onAddOrReplacePayment={data.onAddOrReplacePayment}
          onDeletePayment={data.onDeletePayment}
        />
      ))}
    </>
  );
}
